// components/slides/SlidesWelcome.tsx
'use client';

import React, { useCallback, useEffect, useState } from 'react';
import { useKernel } from '@/kernel/Kernel';
import { useKernelStore } from '@/kernel/kernelStores';
import { canAccessSlides, isSlidesTrialUser } from '#lib/access/slidesAccess';
import { useSlidesStore } from './slidesStore';
import { SlidesTrialNotice, slidesLaunchTourLabel } from './SlidesTrialNotice';
import { useNoticeAcknowledgement } from '@/components/portal/useNoticeAcknowledgement';
import { SlidesTourButton } from './SlidesTour';
import { Sparkles } from '@/components/ui/lucide-memo';

// A new value shows the welcome once more to every pilot account: the pilot launch, 2026-10-07.
const RELEASE_VERSION = '2026-10-pilot-launch';

export const SlidesWelcomeButton = ({ iconOnly = false }: { iconOnly?: boolean }) => {
  const { kernel } = useKernel();
  const user = useKernelStore((state) => state.userContext);
  const replay = useCallback(() => {
    const currentUser = kernel.current?.getUserContext();
    if (currentUser && canAccessSlides(currentUser)) useSlidesStore.setState({ welcomeUserId: currentUser.dbUserId, tour: null });
  }, []);
  if (!canAccessSlides(user)) return null;
  if (iconOnly) return <button type="button" data-slides-welcome onClick={replay} aria-label="What's new" title="What's new"
    className='flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[#B4B4B4] transition-colors hover:bg-white/[0.08] hover:text-[#ECECEC] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white'>
    <Sparkles size={18} aria-hidden="true" />
  </button>;
  return <button type="button" data-slides-welcome onClick={replay}
    className="h-8 shrink-0 rounded-full bg-transparent px-3 text-[#B4B4B4] hover:bg-white/[0.08] hover:text-[#ECECEC] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
    style={{ fontSize: 14 }}>
    What&apos;s new
  </button>;
};

// SlidesView keys this presentation by account and unmounts it outside Slides.
export const SlidesWelcome = () => {
  const { kernel } = useKernel();
  const user = useKernelStore((state) => state.userContext);
  const { ready, accepted, accept } = useNoticeAcknowledgement(`slides:${RELEASE_VERSION}`);
  const requestedUserId = useSlidesStore((state) => state.welcomeUserId);
  const touring = useSlidesStore((state) => state.tour !== null);
  const [dismissedHere, setDismissedHere] = useState(false);
  const userId = user.dbUserId;
  const trial = isSlidesTrialUser(user);
  const language = useKernelStore((state) => state.userPreferences?.language === 'FRENCH' ? 'fr' as const : 'en' as const);
  const showUpdates = requestedUserId === userId || (trial && ready && !accepted && !dismissedHere);

  useEffect(() => () => {
    if (useSlidesStore.getState().welcomeUserId === userId) useSlidesStore.setState({ welcomeUserId: null });
  }, [userId]);

  const close = () => {
    setDismissedHere(true);
    useSlidesStore.setState({ welcomeUserId: null });
    const k = kernel.current;
    if (!k || k.getUserContext().dbUserId !== userId) return;
    accept();
    k.logUserAction('Slides Welcome Dismissed', { release: showUpdates ? RELEASE_VERSION : null });
  };

  if (!ready || !userId || !canAccessSlides(user) || touring || !showUpdates) return null;
  return <SlidesTrialNotice language={language} onClose={close} actions={<SlidesTourButton label={slidesLaunchTourLabel(language)} onStart={close} />} />;
};
